import { environment } from 'src/environments/environment';
import { NgModule, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { MaterialModule } from './core/modules/materials.module';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { GlobalSearchComponent } from './core/lib/ui/global-search/global-search.component';

import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
//import { ErrorIntercept } from './shared/http_service/error.interceptor';
import { LocationSelectorModule } from './shared/location-selector/location-selector.module';
// import { WeatherForecastModule } from './shared_components/weather-forecast/weather-forecast.module';
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
} from '@azure/msal-browser';
import {
  MsalGuard,
  MsalInterceptor,
  MsalBroadcastService,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
  MsalRedirectComponent,
} from '@azure/msal-angular';
import { UserLoginModule } from './routes/user-login/user-login.module';
import { LocationStrategy, PathLocationStrategy } from '@angular/common';

/**
_______________________________________________________________________________
Determines if the current browser is Internet Explorer.
@returns boolean indicating if the browser is IE
_______________________________________________________________________________
*/
const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1;

/**
_______________________________________________________________________________
Logger callback for MSAL authentication library. Currently disabled.
@param logLevel - The severity level of the log message
@param message - The log message content
_______________________________________________________________________________
*/
export function loggerCallback(logLevel: LogLevel, message: string) {
  //console.log(message);
}

/**
_______________________________________________________________________________
Factory function that creates and configures the MSAL PublicClientApplication
instance for Azure AD authentication. Configures client ID, authority, redirect
URI, cache location, and logging options.
@returns Configured IPublicClientApplication instance
_______________________________________________________________________________
*/
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: '888a31ce-59d3-4a12-a596-8d62f641b505',
      authority:
        'https://login.microsoftonline.com/6f411982-cf23-4e45-9449-4b607b7a88ec',
      redirectUri: environment.redirect,
      // clientCapabilities: ['CP1'],
    },
    cache: {
      cacheLocation: BrowserCacheLocation.SessionStorage,
      storeAuthStateInCookie: isIE, // set to true for IE 11
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

/**
_______________________________________________________________________________
Factory function that configures the MSAL HTTP interceptor. Defines which API
endpoints require authentication tokens and maps them to their required scopes.
All GTLC API endpoints across environments are configured here.
@returns MsalInterceptorConfiguration with protected resource map
_______________________________________________________________________________
*/
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', [
    'user.read',
  ]);
  protectedResourceMap.set('https://dev.gtlcapp.ca/rest/gtlc-cms_dev', [
    'api://888a31ce-59d3-4a12-a596-8d62f641b505/GTLC_Application',
  ]);

  protectedResourceMap.set('https://uat.gtlcapp.ca/rest/gtlc-cms_uat/', [
    'api://888a31ce-59d3-4a12-a596-8d62f641b505/GTLC_Application',
  ]);
  protectedResourceMap.set('https://rc.gtlcapp.ca/rest/gtlc-cms_rc/', [
    'api://888a31ce-59d3-4a12-a596-8d62f641b505/GTLC_Application',
  ]);
  protectedResourceMap.set('https://management.gtlcapp.ca/rest/gtlc-cms/', [
    'api://888a31ce-59d3-4a12-a596-8d62f641b505/GTLC_Application',
  ]);
  protectedResourceMap.set('http://localhost:8080/rest/', [
    'api://888a31ce-59d3-4a12-a596-8d62f641b505/GTLC_Application',
  ]);

  protectedResourceMap.set('http://localhost:8502/rest/gtlc-cms/', [
    'api://888a31ce-59d3-4a12-a596-8d62f641b505/GTLC_Application',
  ]);
  protectedResourceMap.set('http://localhost:8500/rest/gtlc-cms/', [
    'api://888a31ce-59d3-4a12-a596-8d62f641b505/GTLC_Application',
  ]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

/**
_______________________________________________________________________________
Factory function that initializes the MSAL PublicClientApplication instance.
This is required for MSAL v2+ before any API calls can be made.
@returns Promise that resolves when MSAL is initialized
_______________________________________________________________________________
*/
export function MSALInitializeFactory(
  msalInstance: IPublicClientApplication,
): () => Promise<void> {
  return () => msalInstance.initialize();
}

/**
_______________________________________________________________________________
Factory function that configures the MSAL route guard. Defines how the guard
should handle unauthenticated users (redirect to login) and what scopes to
request during authentication.
@returns MsalGuardConfiguration for route protection
_______________________________________________________________________________
*/
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read'],
    },
  };
}

/**
_______________________________________________________________________________
Root Angular module that bootstraps the GTLC CMS application. Configures Azure
AD authentication via MSAL, sets up HTTP interceptors for token injection, and
imports all core modules required for the application to function.
_______________________________________________________________________________
*/
@NgModule({
  declarations: [AppComponent, GlobalSearchComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    MaterialModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    LocationSelectorModule,
    // WeatherForecastModule,
    UserLoginModule,
    MsalModule,
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: MSALInitializeFactory,
      deps: [MSAL_INSTANCE],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    { provide: Window, useValue: window },
    { provide: LocationStrategy, useClass: PathLocationStrategy },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
